import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";

import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { formattedDateTime } from "../../util/date";
import { SENT_NOTIFICATIONS, USER_NOTIFICATIONS } from "../../components/notification/queries/UserNotifications";
import { NOTIFICATION_TYPES } from "../../constants/vars";
import NotificationDetailsModal from "../../components/notification/NotificationDetailsModal";

import NotificationComposeDrawer from "../../components/notification/NotificationComposeDrawer";
import NotificationFiltersBar from "../../components/notification/NotificationFiltersBar";

import { FLAT_ORGANIZATIONS } from "../../components/organization/queries/GetOrganizations";

import {
    CREATE_MASSIVE_NOTIFICATION,
    MARK_NOTIFICATION_AS_READ,
    MARK_ALL_NOTIFICATIONS_AS_READ,
    DELETE_NOTIFICATION,
    DELETE_ALL_NOTIFICATIONS,
} from "../../components/notification/mutations/notifications";

import { confirmDelete } from "../../components/common/confirmDelete";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const SMALL_DT_FORMAT = "LLL d '•' h:mm a";

const truncate = (text, max = 120) => {
    const s = String(text || "").trim();
    if (!s) return "";
    if (s.length <= max) return s;
    return s.slice(0, max - 1) + "…";
};

export default function NotificationsPage() {
    const { t } = useTranslation();

    const {
        hasAnyOrgProOrElite,
        hasAnyOrgElite,
    } = useActiveProfileOrgProOrElite();

    const { activeProfile } = useActiveProfile();
    const profileId = activeProfile?.id;

    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [notificationView, setNotificationView] = useState("received");
    const isSentView = notificationView === "sent";

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const openDetails = useCallback((row) => {
        setSelectedRow(row || null);
        setDetailsOpen(true);
    }, []);

    const closeDetails = useCallback(() => {
        setDetailsOpen(false);
        setSelectedRow(null);
    }, []);

    const [uiStatusFilter, setUiStatusFilter] = useState("unread");
    const [uiTypeFilter, setUiTypeFilter] = useState([]);

    const [appliedIsRead, setAppliedIsRead] = useState(false);
    const [appliedTypes, setAppliedTypes] = useState([]);

    const typeOptions = useMemo(() => {
        const raw = NOTIFICATION_TYPES;

        const normalizeOne = (value) => {
            const v = String(value || "").trim();
            if (!v) return null;

            return {
                value: v,
                label: t(`notificationTypes.${v}`),
            };
        };

        if (Array.isArray(raw)) {
            return raw
                .map((item) => {
                    if (typeof item === "string") return normalizeOne(item);
                    if (item && typeof item === "object") return normalizeOne(item.value);
                    return null;
                })
                .filter(Boolean);
        }

        if (raw && typeof raw === "object") {
            return Object.keys(raw)
                .map((key) => normalizeOne(key))
                .filter(Boolean);
        }

        return [];
    }, [t]);

    const timezone = useMemo(
        () => Intl.DateTimeFormat().resolvedOptions().timeZone,
        []
    );

    const commonQueryVariables = useMemo(
        () => ({
            profileId,
            notificationType: appliedTypes.length ? appliedTypes : undefined,
            page,
            pageSize,
        }),
        [profileId, appliedTypes, page, pageSize]
    );

    const receivedQueryVariables = useMemo(
        () => ({
            ...commonQueryVariables,
            isRead: appliedIsRead,
        }),
        [commonQueryVariables, appliedIsRead]
    );

    const { data: receivedData, loading: receivedLoading, refetch: refetchReceived } = useQuery(USER_NOTIFICATIONS, {
        variables: receivedQueryVariables,
        skip: !profileId || isSentView,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
    });

    const { data: sentData, loading: sentLoading, refetch: refetchSent } = useQuery(SENT_NOTIFICATIONS, {
        variables: {
            ...commonQueryVariables,
        },
        skip: !profileId || !isSentView,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
    });

    const loading = isSentView ? sentLoading : receivedLoading;
    const refetch = isSentView ? refetchSent : refetchReceived;
    const payload = isSentView ? sentData?.sentNotifications : receivedData?.userNotifications;
    const items = useMemo(() => {
        const objects = payload?.objects || [];
        if (!isSentView) return objects;

        return objects.map((notification) => ({
            id: notification?.id,
            isRead: true,
            readAt: null,
            notification,
            isSent: true,
        }));
    }, [payload?.objects, isSentView]);
    const pages = payload?.pages || 1;
    const totalCount = payload?.totalCount ?? items.length;

    const headers = useMemo(
        () => [
            t("notificationsPage.table.headers.sender"),
            t("notificationsPage.table.headers.title"),
            t("notificationsPage.table.headers.message"),
            t("notificationsPage.table.headers.type"),
            t("notificationsPage.table.headers.created"),
            t("notificationsPage.table.headers.actions"),
        ],
        [t]
    );

    const doRefetch = useCallback(
        (opts = {}) => {
            const nextPage = Number.isFinite(opts.page) ? opts.page : page;
            const variables = {
                profileId,
                notificationType: appliedTypes.length ? appliedTypes : undefined,
                page: nextPage,
                pageSize,
            };

            refetch?.(isSentView ? variables : { ...variables, isRead: appliedIsRead });
        },
        [refetch, isSentView, profileId, appliedIsRead, appliedTypes, page, pageSize]
    );

    const onPageChange = useCallback(
        (p) => {
            const next = Math.max(1, parseInt(p || 1, 10));
            setPage(next);
        },
        []
    );

    const changeNotificationView = useCallback((nextView) => {
        setNotificationView(nextView);
        setPage(1);
    }, []);

    const resetFilters = useCallback(() => {
        setUiStatusFilter("all");
        setUiTypeFilter([]);
    }, []);

    const applyFilters = useCallback(() => {
        const nextIsRead =
            uiStatusFilter === "unread"
                ? false
                : uiStatusFilter === "read"
                    ? true
                    : undefined;

        const nextTypes = Array.isArray(uiTypeFilter)
            ? uiTypeFilter.filter(Boolean)
            : [];

        setAppliedIsRead(nextIsRead);
        setAppliedTypes(nextTypes);

        const nextPage = 1;
        setPage(nextPage);

        const variables = {
            profileId,
            notificationType: nextTypes.length ? nextTypes : undefined,
            page: nextPage,
            pageSize,
        };

        refetch?.(isSentView ? variables : { ...variables, isRead: nextIsRead });
    }, [uiStatusFilter, uiTypeFilter, refetch, isSentView, profileId, pageSize]);

    const [markNotificationAsRead, { loading: markingOne }] = useMutation(
        MARK_NOTIFICATION_AS_READ,
        {
            onError: (err) =>
                toast.error(
                    getLocalizedGraphQLError(err, t, "notificationsPage.toasts.failedMarkRead")
                ),
        }
    );

    const [deleteNotification, { loading: deletingOne }] = useMutation(
        DELETE_NOTIFICATION,
        {
            onError: (err) =>
                toast.error(
                    getLocalizedGraphQLError(err, t, "notificationsPage.toasts.failedDelete")
                ),
        }
    );

    const [markAllNotificationsAsRead, { loading: markingAll }] = useMutation(
        MARK_ALL_NOTIFICATIONS_AS_READ,
        {
            onError: (err) =>
                toast.error(
                    getLocalizedGraphQLError(err, t, "notificationsPage.toasts.failedMarkAllRead")
                ),
        }
    );

    const [deleteAllNotifications, { loading: deletingAll }] = useMutation(
        DELETE_ALL_NOTIFICATIONS,
        {
            onError: (err) =>
                toast.error(
                    getLocalizedGraphQLError(err, t, "notificationsPage.toasts.failedDeleteAll")
                ),
        }
    );

    const busyAnyAction = loading || markingOne || deletingOne || markingAll || deletingAll;

    const onMarkOneAsRead = useCallback(
        async (row) => {
            if (!profileId) return toast.error(t("notificationsPage.toasts.missingActiveProfile"));
            const notificationId = row?.notification?.id;
            if (!notificationId) return toast.error(t("notificationsPage.toasts.missingNotificationId"));

            const res = await markNotificationAsRead({
                variables: { notificationId, profileId: parseInt(String(profileId), 10) },
            });

            const ok = res?.data?.markNotificationAsRead?.success;
            if (ok) {
                toast.success(t("notificationsPage.toasts.markedRead"));
                doRefetch();
            } else {
                toast.error(t("notificationsPage.toasts.couldNotMarkRead"));
            }
        },
        [profileId, markNotificationAsRead, doRefetch, t]
    );

    const onDeleteOne = useCallback(
        async (row) => {
            if (!profileId) return toast.error(t("notificationsPage.toasts.missingActiveProfile"));
            const notificationId = row?.notification?.id;
            if (!notificationId) return toast.error(t("notificationsPage.toasts.missingNotificationId"));

            const res = await deleteNotification({
                variables: { notificationId, profileId: parseInt(String(profileId), 10) },
            });

            const ok = res?.data?.deleteNotification?.success;
            if (ok) {
                toast.success(t("notificationsPage.toasts.deleted"));
                doRefetch();
            } else {
                toast.error(t("notificationsPage.toasts.couldNotDelete"));
            }
        },
        [profileId, deleteNotification, doRefetch, t]
    );

    const onMarkAllAsRead = useCallback(async () => {
        if (!profileId) return toast.error(t("notificationsPage.toasts.missingActiveProfile"));

        const ok = await confirmDelete({
            title: t("notificationsPage.confirmations.markAllTitle"),
            message: t("notificationsPage.confirmations.markAllMessage"),
            confirmLabel: t("notificationsPage.confirmations.markAllConfirm"),
            danger: false,
        });

        if (!ok) return;

        const res = await markAllNotificationsAsRead({
            variables: { profileId: String(profileId) },
        });

        const success = res?.data?.markAllNotificationsAsRead?.success;
        if (success) {
            toast.success(t("notificationsPage.toasts.allMarkedRead"));
            if (page === 1) doRefetch({ page: 1 });
            else setPage(1);
        } else {
            toast.error(t("notificationsPage.toasts.couldNotMarkAllRead"));
        }
    }, [profileId, markAllNotificationsAsRead, doRefetch, page, t]);

    const onDeleteAll = useCallback(async () => {
        if (!profileId) return toast.error(t("notificationsPage.toasts.missingActiveProfile"));

        const ok = await confirmDelete({
            title: t("notificationsPage.confirmations.deleteAllTitle"),
            message: t("notificationsPage.confirmations.deleteAllMessage"),
            confirmLabel: t("notificationsPage.confirmations.deleteAllConfirm"),
            danger: true,
        });

        if (!ok) return;

        const res = await deleteAllNotifications({
            variables: { profileId: parseInt(String(profileId), 10) },
        });

        const success = res?.data?.deleteAllNotifications?.success;
        if (success) {
            toast.success(t("notificationsPage.toasts.allDeleted"));
            if (page === 1) doRefetch({ page: 1 });
            else setPage(1);
        } else {
            toast.error(t("notificationsPage.toasts.couldNotDeleteAll"));
        }
    }, [profileId, deleteAllNotifications, doRefetch, page, t]);

    const renderRow = useCallback(
        (row) => {
            const n = row?.notification || {};
            const title = n?.title || t("notificationsPage.table.notificationFallback");
            const message = n?.message || "";
            const rawType = n?.type;
            const typeKey = rawType ? String(rawType).toLowerCase() : null;
            const type = typeKey
                ? t(`notificationTypes.${typeKey}`, {}, String(rawType))
                : t("notificationsPage.table.emptyType");
            const senderName = n?.sender?.fullName || t("notificationsPage.table.systemSender");
            const createdAt = n?.createdAt;

            const createdAtLabel = createdAt
                ? formattedDateTime(createdAt, timezone, "en", SMALL_DT_FORMAT)
                : t("notificationsPage.table.emptyType");

            const isUnread = row?.isRead === false;
            const isSent = row?.isSent === true;
            const statusLabel = isSent
                ? t("notificationsPage.row.sentTitle")
                : isUnread
                    ? t("notificationsPage.row.unreadTitle")
                    : t("notificationsPage.row.readTitle");
            const iconClass = isSent
                ? "fa-paper-plane"
                : isUnread
                    ? "fa-envelope"
                    : "fa-envelope-open";

            return (
                <tr
                    key={row?.id}
                    className={`kbola-clickable-row ${isUnread ? "kbola-notif-unread-row" : "kbola-notif-read-row"}`}
                >
                    <td
                        style={{ whiteSpace: "nowrap" }}
                        className="kbola-clickable-cell"
                        onClick={() => openDetails(row)}
                    >
                        <div className="kbola-notif-cell-title" title={senderName}>
                            <span
                                className={`kbola-notif-mail ${isUnread ? "is-unread" : "is-read"}`}
                                aria-label={statusLabel}
                                title={statusLabel}
                            >
                                <i className={`fas ${iconClass}`} />
                            </span>

                            <span className={`kbola-notif-title-text ${isUnread ? "is-unread" : ""}`}>
                                {senderName}
                            </span>
                        </div>
                    </td>

                    <td
                        className="kbola-clickable-cell"
                        style={{ maxWidth: 320 }}
                        onClick={() => openDetails(row)}
                    >
                        <div className="kbola-notif-cell-title" title={title}>
                            <span className={`kbola-notif-title-text ${isUnread ? "is-unread" : ""}`}>
                                {title}
                            </span>
                        </div>
                    </td>

                    <td
                        className="kbola-clickable-cell"
                        style={{ maxWidth: 520 }}
                        onClick={() => openDetails(row)}
                    >
                        <div className="kbola-notif-cell-message" title={message}>
                            {truncate(message, 50)}
                        </div>
                    </td>

                    <td
                        className="kbola-clickable-cell"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => openDetails(row)}
                    >
                        <span className="kbola-notif-type">{type}</span>
                    </td>

                    <td
                        className="kbola-clickable-cell"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => openDetails(row)}
                    >
                        <span
                            className="kbola-notif-created"
                            title={createdAt ? formattedDateTime(createdAt, timezone) : ""}
                        >
                            {createdAtLabel}
                        </span>
                    </td>

                    <td style={{ whiteSpace: "nowrap", width: 1 }}>
                        <div className="kbola-notif-actions">
                            {isSent ? (
                                <span className="text-muted small">
                                    {t("notificationsPage.actions.noActions")}
                                </span>
                            ) : isUnread ? (
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm kbola-notif-actionbtn"
                                    onClick={() => onMarkOneAsRead(row)}
                                    disabled={busyAnyAction}
                                    title={t("notificationsPage.actions.markAsRead")}
                                >
                                    <i className="fas fa-check-double mr-1" />
                                    {t("notificationsPage.actions.read")}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm kbola-notif-actionbtn"
                                    disabled
                                    title={t("notificationsPage.actions.alreadyRead")}
                                >
                                    <i className="fas fa-check mr-1" />
                                    {t("notificationsPage.actions.read")}
                                </button>
                            )}

                            {!isSent ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm kbola-notif-actionbtn"
                                    onClick={() => onDeleteOne(row)}
                                    disabled={busyAnyAction}
                                    title={t("notificationsPage.actions.delete")}
                                >
                                    <i className="fas fa-trash mr-1" />
                                    {t("notificationsPage.actions.delete")}
                                </button>
                            ) : null}
                        </div>
                    </td>
                </tr>
            );
        },
        [timezone, onMarkOneAsRead, onDeleteOne, busyAnyAction, openDetails, t]
    );

    const subtitle = useMemo(() => {
        if (!activeProfile) return t("notificationsPage.page.inbox");

        const fullName = `${activeProfile?.firstName ?? ""} ${activeProfile?.lastName ?? ""}`.trim();
        return t("notificationsPage.page.subtitle", {
            count: totalCount,
            suffix: totalCount === 1 ? "" : "s",
            name: fullName,
        });
    }, [activeProfile, totalCount, t]);

    const canCompose = useMemo(() => hasAnyOrgProOrElite, [hasAnyOrgProOrElite]);
    const eliteNotifications = hasAnyOrgElite;

    const [composeOpen, setComposeOpen] = useState(false);
    const [composeTitle, setComposeTitle] = useState("");
    const [composeMessage, setComposeMessage] = useState("");

    const [segOrg, setSegOrg] = useState("all");
    const [segDivision, setSegDivision] = useState("all");
    const [segSeason, setSegSeason] = useState("all");
    const [segTeams, setSegTeams] = useState([]);
    const [audience, setAudience] = useState(eliteNotifications ? "all" : "managers");

    const audienceLabel = useMemo(() => {
        switch (audience) {
            case "managers":
                return t("notificationsPage.compose.audienceManagersOnly");
            case "referees":
                return t("notificationsPage.compose.audienceRefereesOnly");
            case "all":
            default:
                return t("notificationsPage.compose.audienceEveryone");
        }
    }, [audience, t]);

    const { data: orgTreeData } = useQuery(FLAT_ORGANIZATIONS, {
        variables: { profileId, page: 1, pageSize: 100 },
        skip: !profileId || !composeOpen,
        fetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    const orgObjects = useMemo(
        () => orgTreeData?.organizations?.objects ?? [],
        [orgTreeData?.organizations?.objects]
    );

    const findOrgBySlug = useCallback(
        (slug) => orgObjects.find((o) => String(o?.slug) === String(slug)),
        [orgObjects]
    );

    const allDivisions = useMemo(() => {
        return orgObjects.flatMap((o) =>
            (o?.divisions || []).map((d) => ({ org: o, div: d }))
        );
    }, [orgObjects]);

    const findDivisionById = useCallback(
        (divId) => allDivisions.find((x) => String(x?.div?.id) === String(divId)) || null,
        [allDivisions]
    );

    const selectedOrg = useMemo(
        () => (segOrg === "all" ? null : findOrgBySlug(segOrg)),
        [segOrg, findOrgBySlug]
    );

    const selectedDivision = useMemo(() => {
        if (segDivision === "all") return null;
        return findDivisionById(segDivision);
    }, [segDivision, findDivisionById]);

    const selectedSeason = useMemo(() => {
        if (segSeason === "all") return null;

        if (selectedDivision?.div?.seasons?.length) {
            return (
                selectedDivision.div.seasons.find((s) => String(s?.id) === String(segSeason)) ||
                null
            );
        }

        const divsInScope = selectedOrg?.divisions?.length
            ? selectedOrg.divisions
            : orgObjects.flatMap((o) => o?.divisions || []);

        for (const d of divsInScope) {
            const hit = (d?.seasons || []).find((s) => String(s?.id) === String(segSeason));
            if (hit) return hit;
        }
        return null;
    }, [segSeason, selectedDivision, selectedOrg, orgObjects]);

    const orgOptions = useMemo(() => {
        const rows = orgObjects.map((o) => ({
            value: String(o?.slug),
            label: o?.name || String(o?.slug),
        }));
        return rows;
    }, [orgObjects]);

    const divisionOptions = useMemo(() => {
        const base = [{ value: "all", label: t("notificationsPage.compose.allDivisions") }];

        const divs = selectedOrg?.divisions?.length
            ? selectedOrg.divisions.map((d) => ({ d, orgName: selectedOrg?.name }))
            : allDivisions.map(({ org, div }) => ({ d: div, orgName: org?.name }));

        const rows = divs
            .map(({ d, orgName }) => ({
                value: String(d?.id),
                label: selectedOrg
                    ? d?.name || String(d?.id)
                    : `${orgName || t("notificationsPage.compose.orgFallback")} • ${d?.name || String(d?.id)}`,
            }))
            .filter((x) => x.value && x.label);

        return [...base, ...rows];
    }, [selectedOrg, allDivisions, t]);

    const seasonOptions = useMemo(() => {
        const base = [{ value: "all", label: t("notificationsPage.compose.allSeasons") }];
        let seasons = [];

        if (selectedDivision?.div) {
            seasons = (selectedDivision.div.seasons || []).map((s) => ({
                s,
                prefix: selectedOrg ? null : selectedDivision?.org?.name,
                divName: selectedDivision?.div?.name,
            }));
        } else if (selectedOrg?.divisions?.length) {
            seasons = selectedOrg.divisions.flatMap((d) =>
                (d?.seasons || []).map((s) => ({
                    s,
                    prefix: null,
                    divName: d?.name,
                }))
            );
        } else {
            seasons = orgObjects.flatMap((o) =>
                (o?.divisions || []).flatMap((d) =>
                    (d?.seasons || []).map((s) => ({
                        s,
                        prefix: o?.name,
                        divName: d?.name,
                    }))
                )
            );
        }

        const rows = seasons
            .map(({ s, prefix, divName }) => ({
                value: String(s?.id),
                label: prefix
                    ? `${prefix} • ${divName || t("notificationsPage.compose.divisionFallback")} • ${s?.name || String(s?.id)}`
                    : s?.name || String(s?.id),
            }))
            .filter((x) => x.value && x.label);

        return [...base, ...rows];
    }, [selectedDivision, selectedOrg, orgObjects, t]);

    const teamOptions = useMemo(() => {
        const mapTeam = (teamRef) => {
            const team = teamRef?.team;
            if (!team?.id) return null;
            return { value: String(team.id), label: team.name || String(team.id) };
        };

        let teams = [];
        if (selectedSeason) {
            teams = (selectedSeason?.teams || []).map(mapTeam).filter(Boolean);
        } else if (selectedDivision?.div) {
            teams = (selectedDivision.div.seasons || [])
                .flatMap((s) => (s?.teams || []).map(mapTeam))
                .filter(Boolean);
        } else if (selectedOrg?.divisions?.length) {
            teams = selectedOrg.divisions
                .flatMap((d) => (d?.seasons || []).flatMap((s) => (s?.teams || []).map(mapTeam)))
                .filter(Boolean);
        } else {
            teams = orgObjects
                .flatMap((o) =>
                    (o?.divisions || []).flatMap((d) =>
                        (d?.seasons || []).flatMap((s) => (s?.teams || []).map(mapTeam))
                    )
                )
                .filter(Boolean);
        }

        const seen = new Set();
        const out = [];
        for (const team of teams) {
            if (!team?.value || seen.has(team.value)) continue;
            seen.add(team.value);
            out.push(team);
        }
        return out;
    }, [selectedSeason, selectedDivision, selectedOrg, orgObjects]);

    const segOrgLabel = useMemo(() => {
        const hit = orgOptions.find((o) => String(o.value) === String(segOrg));
        return hit?.label ?? String(segOrg);
    }, [segOrg, orgOptions]);

    const segDivisionLabel = useMemo(() => {
        if (segDivision === "all") return t("notificationsPage.compose.allDivisions");
        const hit = divisionOptions.find((o) => String(o.value) === String(segDivision));
        return hit?.label ?? String(segDivision);
    }, [segDivision, divisionOptions, t]);

    const segSeasonLabel = useMemo(() => {
        if (segSeason === "all") return t("notificationsPage.compose.allSeasons");
        const hit = seasonOptions.find((o) => String(o.value) === String(segSeason));
        return hit?.label ?? String(segSeason);
    }, [segSeason, seasonOptions, t]);

    const segTeamsLabel = useMemo(() => {
        const ids = Array.isArray(segTeams) ? segTeams.map(String) : [];
        if (!ids.length) return t("notificationsPage.compose.allTeams");

        const labelMap = new Map(teamOptions.map((team) => [String(team.value), team.label]));
        const names = ids.map((id) => labelMap.get(id)).filter(Boolean);

        if (!names.length) {
            return t("notificationsPage.compose.selectedCount", { count: ids.length });
        }

        const shown = names.slice(0, 3);
        const remaining = Math.max(0, names.length - shown.length);
        return remaining ? `${shown.join(", ")} +${remaining}` : shown.join(", ");
    }, [segTeams, teamOptions, t]);

    const setSegOrgCascade = useCallback((next) => {
        const value = String(next || "all");
        setSegOrg(value);
        setSegDivision("all");
        setSegSeason("all");
        setSegTeams([]);
    }, []);

    const setSegDivisionCascade = useCallback((next) => {
        const value = String(next || "all");
        setSegDivision(value);
        setSegSeason("all");
        setSegTeams([]);
    }, []);

    const setSegSeasonCascade = useCallback((next) => {
        const value = String(next || "all");
        setSegSeason(value);
        setSegTeams([]);
    }, []);

    const openCompose = useCallback(() => {
        if (!canCompose) return;
        setComposeOpen(true);
    }, [canCompose]);

    const closeCompose = useCallback(() => setComposeOpen(false), []);
    const cancelCompose = useCallback(() => setComposeOpen(false), []);

    const [createMassiveNotification, { loading: sending }] = useMutation(
        CREATE_MASSIVE_NOTIFICATION,
        {
            onError: (err) =>
                toast.error(getLocalizedGraphQLError(err, t, "notificationsPage.toasts.failedSend")),
            onCompleted: (res) => {
                const payloadResult = res?.createMassiveNotification;
                if (payloadResult?.ok) {
                    toast.success(
                        t("notificationsPage.toasts.sentCreated", {
                            count: payloadResult?.createdUserNotifications || 0,
                        })
                    );
                    if (page === 1) doRefetch({ page: 1 });
                    else setPage(1);
                } else {
                    toast.error(t("notificationsPage.toasts.sendFailed"));
                }
            },
        }
    );

    const sendCompose = useCallback(() => {
        if (!profileId) return toast.error(t("notificationsPage.toasts.missingActiveProfile"));

        const titleValue = String(composeTitle || "").trim();
        const messageValue = String(composeMessage || "").trim();

        if (!titleValue) return toast.error(t("notificationsPage.toasts.titleRequired"));
        if (!messageValue) return toast.error(t("notificationsPage.toasts.messageRequired"));
        if (!segOrg) return toast.error(t("notificationsPage.toasts.selectOrganization"));

        const safeTeams = Array.isArray(segTeams) ? segTeams : [];
        const teamIds = safeTeams
            .map((x) => parseInt(String(x), 10))
            .filter((n) => Number.isFinite(n));

        const seasonId = segSeason !== "all" ? parseInt(String(segSeason), 10) : undefined;
        const divisionId = segDivision !== "all" ? parseInt(String(segDivision), 10) : undefined;

        const input = {
            profileId,
            title: titleValue,
            message: messageValue,
            orgSlug: String(segOrg),
            seasonId: Number.isFinite(seasonId) ? seasonId : undefined,
            divisionId: Number.isFinite(divisionId) ? divisionId : undefined,
            teamIds: teamIds.length ? teamIds : undefined,
            audience: String(audience || "all"),
        };

        createMassiveNotification({ variables: { input } });
        setComposeOpen(false);
    }, [
        createMassiveNotification,
        profileId,
        composeTitle,
        composeMessage,
        segOrg,
        segDivision,
        segSeason,
        segTeams,
        audience,
        t,
    ]);

    return (
        <div className="content-wrapper" style={{ padding: 16 }}>
            <div className="kbola-notif-view-tabs" role="tablist" aria-label={t("notificationsPage.tabs.label")}>
                <button
                    type="button"
                    role="tab"
                    aria-selected={!isSentView}
                    className={`btn kbola-notif-view-tab ${!isSentView ? "btn-dark is-active" : "btn-light"}`}
                    onClick={() => changeNotificationView("received")}
                    disabled={loading}
                >
                    <i className="fas fa-inbox mr-2" />
                    {t("notificationsPage.tabs.received")}
                </button>

                <button
                    type="button"
                    role="tab"
                    aria-selected={isSentView}
                    className={`btn kbola-notif-view-tab ${isSentView ? "btn-dark is-active" : "btn-light"}`}
                    onClick={() => changeNotificationView("sent")}
                    disabled={loading}
                >
                    <i className="fas fa-paper-plane mr-2" />
                    {t("notificationsPage.tabs.sent")}
                </button>
            </div>

            <NotificationFiltersBar
                loading={loading}
                uiStatusFilter={uiStatusFilter}
                setUiStatusFilter={setUiStatusFilter}
                uiTypeFilter={uiTypeFilter}
                setUiTypeFilter={setUiTypeFilter}
                typeOptions={typeOptions}
                onReset={resetFilters}
                onApply={applyFilters}
                onCompose={openCompose}
                showCompose={canCompose}
                showStatusFilter={!isSentView}
            />

            <div className="kbola-notif-bulkbar">
                {!isSentView ? (
                    <div className="kbola-notif-bulkbar-left">
                        <button
                            type="button"
                            className="btn btn-light kbola-bulk-btn"
                            onClick={onMarkAllAsRead}
                            disabled={busyAnyAction || !items.length}
                            title={t("notificationsPage.bulk.markAllAsReadTitle")}
                        >
                            <i className="fas fa-check-double mr-2" />
                            {t("notificationsPage.actions.markAllAsRead")}
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-danger kbola-bulk-btn"
                            onClick={onDeleteAll}
                            disabled={busyAnyAction || !items.length}
                            title={t("notificationsPage.bulk.deleteAllTitle")}
                        >
                            <i className="fas fa-trash-alt mr-2" />
                            {t("notificationsPage.actions.deleteAll")}
                        </button>
                    </div>
                ) : null}

                <div className="kbola-notif-bulkbar-right">
                    <button
                        type="button"
                        className="btn btn-light kbola-bulk-btn"
                        onClick={() => doRefetch()}
                        disabled={busyAnyAction}
                        title={t("notificationsPage.bulk.refreshTitle")}
                    >
                        <i className="fas fa-sync-alt mr-2" />
                        {t("notificationsPage.actions.refresh")}
                    </button>
                </div>
            </div>

            <PaginatedFlatTable
                title={t("notificationsPage.page.title")}
                subtitle={subtitle}
                page={page}
                pages={pages}
                items={items}
                loading={loading}
                onPageChange={onPageChange}
                headers={headers}
                renderRow={renderRow}
                loadingOverlay={true}
                className="kbola-notifications-page"
            />

            <NotificationComposeDrawer
                open={composeOpen}
                onClose={closeCompose}
                title={composeTitle}
                setTitle={setComposeTitle}
                message={composeMessage}
                setMessage={setComposeMessage}
                segOrg={segOrg}
                setSegOrg={setSegOrgCascade}
                segDivision={segDivision}
                setSegDivision={setSegDivisionCascade}
                segSeason={segSeason}
                setSegSeason={setSegSeasonCascade}
                segTeams={segTeams}
                setSegTeams={setSegTeams}
                segOrgLabel={segOrgLabel}
                segDivisionLabel={segDivisionLabel}
                segSeasonLabel={segSeasonLabel}
                segTeamsLabel={segTeamsLabel}
                orgOptions={orgOptions}
                divisionOptions={divisionOptions}
                seasonOptions={seasonOptions}
                teamOptions={teamOptions}
                audience={audience}
                setAudience={setAudience}
                audienceLabel={audienceLabel}
                onCancel={cancelCompose}
                onSend={sendCompose}
                sending={sending}
            />

            <NotificationDetailsModal
                open={detailsOpen}
                onClose={closeDetails}
                row={selectedRow}
                timezone={timezone}
            />

            <style>{`
        .kbola-notif-view-tabs{
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin: 0 0 12px;
        }
        .kbola-notif-view-tab{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 14px !important;
        }

        .kbola-notif-bulkbar{
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          margin: 10px 0 12px;
        }
        .kbola-notif-bulkbar-left,
        .kbola-notif-bulkbar-right{
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }
        .kbola-bulk-btn{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 12px !important;
          white-space: nowrap;
        }

        .kbola-notif-cell-title { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .kbola-notif-mail {
          width: 34px; height: 34px; border-radius: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          flex: 0 0 34px; border: 1px solid rgba(0,0,0,0.10);
        }
        .kbola-notif-mail.is-unread { background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.08); color: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.95); }
        .kbola-notif-mail.is-read { background: rgba(0,0,0,0.03); color: rgba(0,0,0,0.55); }

        .kbola-notif-title-text {
          font-weight: 950; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;
        }
        .kbola-notif-cell-message {
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .kbola-notif-created { font-weight: 900; color: rgba(20,21,26,0.70); font-size: 12px; }
        .kbola-notif-type {
          font-size: 12px; font-weight: 950; padding: 6px 10px; border-radius: 999px;
          border: 1px solid rgba(0,0,0,0.10);
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.06);
          color: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.85);
        }
        .kbola-notif-sender {
          font-weight: 900; color: rgba(20,21,26,0.78);
          display: inline-block; max-width: 220px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }

        .kbola-notif-actions{
          display: inline-flex;
          gap: 8px;
          align-items: center;
        }
        .kbola-notif-actionbtn{
          border-radius: 12px !important;
          font-weight: 900 !important;
          padding: 8px 10px !important;
          white-space: nowrap;
        }

        .kbola-notif-read-row td{
          background: rgba(0,0,0,0.012);
        }
        
        .kbola-notif-read-row .kbola-notif-title-text{
          font-weight: 800;
          color: rgba(20,21,26,0.62);
        }
        
        .kbola-notif-read-row .kbola-notif-cell-message{
          font-weight: 700;
          color: rgba(20,21,26,0.50);
        }
        
        .kbola-notif-read-row .kbola-notif-created{
          font-weight: 800;
          color: rgba(20,21,26,0.45);
        }
        
        .kbola-notif-read-row .kbola-notif-type{
          font-weight: 900;
          background: rgba(0,0,0,0.03);
          color: rgba(20,21,26,0.55);
          border-color: rgba(0,0,0,0.08);
        }
        
        .kbola-notif-read-row .kbola-notif-sender{
          font-weight: 800;
          color: rgba(20,21,26,0.52);
        }
        
        .kbola-notif-read-row .kbola-notif-mail.is-read{
          background: rgba(0,0,0,0.02);
          color: rgba(20,21,26,0.45);
          border-color: rgba(0,0,0,0.08);
        }
        .kbola-notif-read-row:hover td{
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.03);
        }

        .kbola-clickable-cell {
          cursor: pointer;
        }
        
        .kbola-clickable-cell:hover {
          text-decoration: underline;
          color: rgba(0, 0, 0, 0.75);
        }
        .kbola-clickable-row:hover td {
          background: rgba(0, 0, 0, 0.03);
        }
      `}</style>
        </div>
    );
}
